import {createAction} from 'typesafe-actions';

import {type OrganizedSettings} from '../../../components/PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../components/PanelMediaBrowser/common';
import {type SemanticLegendPalette} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/utils/colorPalettes';
import {
  type LinePlotSettings,
  type OrganizationPrefix,
  type RunColorOptions,
  type WorkspaceLayoutSettings,
} from '../../../components/WorkspaceDrawer/Settings/types';
import {type ColorPaletteOption} from '../../../util/colors';
import type * as PanelBankConfigTypes from '../panelBankConfig/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type Ref as WorkspaceSettingsRef} from './types';

// Updating setting and changes to view spec must be done in a single action so both can be undone together
export const updateAutoOrganizePrefix = createAction(
  '@view/workspaceSettings/updateAutoOrganizePrefix',
  action =>
    (
      workspaceSettingsRef: WorkspaceSettingsRef,
      panelBankConfigRef: PanelBankConfigTypes.Ref,
      autoOrganizePrefix: OrganizationPrefix | undefined
    ) =>
      action({
        workspaceSettingsRef,
        panelBankConfigRef,
        autoOrganizePrefix,
      })
);

export const updateAutoOrganizePrefixUndo = createAction(
  '@view/workspaceSettings/updateAutoOrganizePrefixUndo',
  action =>
    (
      workspaceSettingsRef: WorkspaceSettingsRef,
      panelBankConfigRef: PanelBankConfigTypes.Ref,
      sectionRefs: PanelBankSectionConfigTypes.Ref[],
      autoOrganizePrefix: OrganizationPrefix | undefined
    ) =>
      action({
        workspaceSettingsRef,
        panelBankConfigRef,
        sectionRefs,
        autoOrganizePrefix,
      })
);

// These are "patch" actions. We're just updating parts of the setting.
export const updateWorkspaceLayoutSettings = createAction(
  '@view/workspaceSettings/layout/update',
  action =>
    (ref: WorkspaceSettingsRef, settings: Partial<WorkspaceLayoutSettings>) =>
      action({ref, settings})
);
export const updateLinePlotWorkspaceSettings = createAction(
  '@view/workspaceSettings/linePlot/update',
  action => (ref: WorkspaceSettingsRef, settings: Partial<LinePlotSettings>) =>
    action({ref, settings})
);
export const updateWorkspaceMediaSettings = createAction(
  '@view/workspaceSettings/media/update',
  action =>
    (ref: WorkspaceSettingsRef, settings: Partial<MediaBrowserPanelConfig>) =>
      action({ref, settings})
);

export const updateColorPaletteOption = createAction(
  '@view/workspaceSettings/colorPalette/update',
  action =>
    (
      ref: WorkspaceSettingsRef,
      newColorPalette: ColorPaletteOption | undefined
    ) =>
      action({ref, newColorPalette})
);

export const updateSemanticLegendPalette = createAction(
  '@view/workspaceSettings/semanticLegendPalette/update',
  action =>
    (ref: WorkspaceSettingsRef, palette: SemanticLegendPalette | undefined) =>
      action({ref, palette})
);

export const updateShouldAutoGeneratePanels = createAction(
  '@view/workspaceSettings/updateShouldAutoGeneratePanels',
  action =>
    (
      workspaceSettingsRef: WorkspaceSettingsRef,
      panelBankConfigRef: PanelBankConfigTypes.Ref,
      value: WorkspaceLayoutSettings['shouldAutoGeneratePanels']
    ) =>
      action({workspaceSettingsRef, panelBankConfigRef, value})
);

// This is a "put" action. We're replacing the entire setting. This is useful for undo.
export const setWorkspaceSettings = createAction(
  '@view/workspaceSettings/set',
  action => (ref: WorkspaceSettingsRef, settings: Partial<OrganizedSettings>) =>
    action({ref, settings})
);

export const unsetWorkspaceMediaSetting = createAction(
  '@view/workspaceSettings/media/unset',
  action => (ref: WorkspaceSettingsRef, key: keyof MediaBrowserPanelConfig) =>
    action({ref, key})
);

export const updateSemanticLegendRunColorOptions = createAction(
  '@view/workspaceSettings/updateSemanticLegendRunColorOptions',
  action =>
    (
      ref: WorkspaceSettingsRef,
      semanticLegendRunColorOptions: RunColorOptions
    ) =>
      action({ref, semanticLegendRunColorOptions})
);
